import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, Check, Loader2, ShieldCheck, CalendarClock, Tag, BellRing } from "lucide-react";
import { sr } from "../../lib/sr";

const TYPE_ICONS = [Tag, CalendarClock, BellRing];

export default function SmsOptIn() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { sr.smsOptInConfig().then(setCfg).catch(() => setCfg(null)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Please check the consent box to opt in."); return; }
    if (form.phone.replace(/\D/g, "").length < 10) { setErr("Please enter a valid phone number."); return; }
    setBusy(true);
    try {
      const r = await sr.submitSmsOptIn({ name: form.name, phone: form.phone, consent: form.consent, source: "web:sms-opt-in" });
      setDone(r);
    } catch (e2) { setErr(e2?.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  const brand = cfg?.brand || "SmartReach AI";
  const brandFooter = cfg?.brand_footer || "SmartReach AI is a brand of Funasia Digital LLC.";
  const noSharing = cfg?.no_sharing || "We will not share your mobile information with third parties for marketing purposes.";
  const freq = cfg?.frequency || "Message frequency varies (up to 6 msgs/month)";
  const consentText = cfg?.consent_text
    || "I agree to receive recurring automated marketing (offers & promotions) and account/service text messages from Funasia Digital LLC (SmartReach AI) at the phone number provided. Consent is not a condition of any purchase. Message frequency varies (up to 6 msgs/month). Message and data rates may apply. Reply HELP for help, STOP to cancel. We will not share your mobile information with third parties for marketing purposes.";
  const useCases = cfg?.use_cases || [
    { name: "Marketing & promotions", sub_use_case: "Marketing",
      description: "Product news, feature launches, and limited-time offers on your SmartReach AI subscription.",
      sample: "Funasia Digital LLC (SmartReach AI): New AI ad tools launched - save 20% on Pro this week. Reply STOP to cancel, HELP for help." },
    { name: "Account & service alerts", sub_use_case: "Account Notifications",
      description: "Account updates, campaign status, and service alerts — like a text when we auto-follow-up on a missed call for you.",
      sample: "Funasia Digital LLC (SmartReach AI): A lead just called and you missed it - we texted them back. Reply STOP to cancel, HELP for help." },
  ];
  const samples = cfg?.samples || useCases.map((u) => u.sample);

  return (
    <div className="sr-root" style={{ minHeight: "100vh", background: "var(--navy, #0B1220)" }} data-testid="sms-optin-page">
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "0 20px 60px" }}>
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 0" }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 9, color: "#fff", textDecoration: "none", fontWeight: 800, fontSize: 18 }}>
            <MessageSquareText size={20} color="#F0B429" /> {brand}
          </Link>
          <Link to="/" style={{ color: "rgba(255,255,255,.7)", textDecoration: "none", fontSize: 13.5 }}>← Home</Link>
        </header>

        <div style={{ textAlign: "center", color: "#fff", marginBottom: 22 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(240,180,41,.15)", color: "#F0B429", padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>
            <ShieldCheck size={14} /> Text message sign-up
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.2, color: "#fff" }}>Get {brand} texts</h1>
          <p data-testid="sms-optin-sender" style={{ color: "rgba(255,255,255,.9)", fontSize: 13.5, fontWeight: 700, margin: "0 0 6px" }}>
            Messages sent by {cfg?.brand_sender || "Funasia Digital LLC (SmartReach AI)"}
          </p>
          <p style={{ color: "rgba(255,255,255,.72)", fontSize: 15, margin: 0 }}>
            Opt in to receive helpful texts. You're always in control — reply <strong>STOP</strong> anytime.
          </p>
        </div>

        {done ? (
          <div className="sr-card" style={{ textAlign: "center", padding: 28 }} data-testid="sms-optin-success">
            <div style={{ width: 56, height: 56, borderRadius: 999, background: "#E7F7EF", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Check size={28} color="#12805C" />
            </div>
            <h2 style={{ margin: "0 0 6px", color: "var(--navy)", fontSize: 20 }}>You're opted in!</h2>
            <p className="sr-muted" style={{ margin: "0 0 4px", fontSize: 14 }}>
              We've recorded your consent for <strong style={{ color: "var(--navy)" }}>{done.phone}</strong>.
            </p>
            <p className="sr-muted" style={{ fontSize: 13 }}>
              {done?.sms?.sent
                ? "A confirmation text is on its way."
                : "You'll get a confirmation text once messaging is live. Reply STOP anytime to cancel."}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="sr-card" style={{ padding: 24 }} data-testid="sms-optin-form">
            <div style={{ display: "grid", gap: 14 }}>
              <label style={{ display: "grid", gap: 5 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Name <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span></span>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
                  data-testid="sms-optin-name" style={{ padding: "11px 13px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 15 }} />
              </label>
              <label style={{ display: "grid", gap: 5 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Mobile phone number</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" inputMode="tel"
                  data-testid="sms-optin-phone" style={{ padding: "11px 13px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 15 }} />
              </label>

              <label style={{ display: "flex", gap: 11, alignItems: "flex-start", background: "var(--surface)", borderRadius: 10, padding: "12px 13px", cursor: "pointer" }}>
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  data-testid="sms-optin-consent" style={{ marginTop: 3, width: 17, height: 17, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: "var(--navy)", lineHeight: 1.5 }}>{consentText}</span>
              </label>

              <div data-testid="sms-optin-no-sharing" style={{ fontSize: 12, color: "var(--navy)", fontWeight: 600, opacity: 0.85, padding: "0 3px", lineHeight: 1.5 }}>
                {noSharing}
              </div>

              {err && <div data-testid="sms-optin-error" style={{ background: "#FEF3F2", color: "#B42318", borderRadius: 8, padding: "9px 12px", fontSize: 13 }}>{err}</div>}

              <button type="submit" className="sr-btn sr-btn-primary" disabled={busy} data-testid="sms-optin-submit"
                style={{ padding: "13px", fontSize: 15, borderRadius: 10, justifyContent: "center" }}>
                {busy ? <Loader2 size={17} className="sr-spin" /> : "Opt in to texts"}
              </button>
            </div>
          </form>
        )}

        <div style={{ marginTop: 22 }}>
          <div style={{ color: "rgba(255,255,255,.8)", fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>What you'll receive</div>
          <div style={{ display: "grid", gap: 8 }}>
            {useCases.map((u, i) => {
              const Icon = TYPE_ICONS[i] || BellRing;
              return (
                <div key={i} data-testid={`sms-optin-usecase-${i}`} style={{ display: "flex", alignItems: "flex-start", gap: 11, background: "rgba(255,255,255,.06)", borderRadius: 10, padding: "12px 13px", color: "rgba(255,255,255,.9)" }}>
                  <Icon size={17} color="#F0B429" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700 }}>{u.name}</div>
                    {u.description && <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.65)", marginTop: 2, lineHeight: 1.5 }}>{u.description}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={{ color: "rgba(255,255,255,.8)", fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>Sample messages</div>
          <div style={{ display: "grid", gap: 8 }}>
            {samples.map((s, i) => (
              <div key={i} data-testid={`sms-optin-sample-${i}`} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(240,180,41,.10)", border: "1px solid rgba(240,180,41,.25)", borderRadius: 10, padding: "11px 13px" }}>
                <MessageSquareText size={15} color="#F0B429" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 12.5, color: "rgba(255,255,255,.85)", lineHeight: 1.55 }}>“{s}”</span>
              </div>
            ))}
          </div>
        </div>

        <p style={{ color: "rgba(255,255,255,.55)", fontSize: 12, lineHeight: 1.6, marginTop: 20, textAlign: "center" }} data-testid="sms-optin-disclaimer">
          {freq}. Message &amp; data rates may apply. Reply <strong style={{ color: "rgba(255,255,255,.8)" }}>HELP</strong> for help and <strong style={{ color: "rgba(255,255,255,.8)" }}>STOP</strong> to cancel at any time.
          By opting in you agree to our{" "}
          <Link to="/terms" style={{ color: "#F0B429" }}>Terms of Service</Link> and{" "}
          <Link to="/privacy" style={{ color: "#F0B429" }}>Privacy Policy</Link>.
          {cfg?.support_email && <> Questions? {cfg.support_email}</>}
        </p>

        <p style={{ color: "rgba(255,255,255,.5)", fontSize: 11.5, textAlign: "center", marginTop: 4 }} data-testid="sms-optin-legal-entity">
          {brandFooter}
        </p>
      </div>
    </div>
  );
}
